import { useEffect, useMemo, useState } from "react";
import { supabase } from "../lib/supabase";
import { MetricChart } from "./MetricChart";
import {
  WINDOWS, describeDelta, inWindow, oneAnswer, windowLabel, type Window,
} from "../lib/metricsWindow";
import type { Measurement } from "../lib/bodyMetrics";
import { wireNumber } from "../lib/wireNumber";
import { longDate } from "../lib/weekLine";
import { dayKeyOf, calendarToday } from "../lib/days";
import { UpdateResultsWindow } from "./UpdateResultsWindow";
import { WEEK_COLUMNS, waterTagText, weekAnswerLines, type WeekLine } from "../lib/weekCheckIn";

/** A reading plus what the N2 weekly check-in said beside it (2026-10-08). */
type Row = Measurement & { week: WeekLine[]; water: string | null };

/* W10d — BODY METRICS: weight and body fat over a window, with the entries beneath.
 *
 * 🔴 TWO CHARTS, NEVER ONE WITH TWO AXES. Weight is kilos around 74 and body fat is a percentage
 * around 20; a dual axis puts two unrelated scales in one frame and every crossing of the lines
 * is an artefact of where the axes were pinned. Small multiples — one measure, one axis, each.
 *
 * ⚠️ READ THROUGH THE POLICY THAT IS LIVE. `body_measurements_coach_read` admits a client's
 * ACTIVE, ACCEPTED coach; there is no `.eq()` here doing the scoping, because a filter in the
 * query would be a second statement of the rule and the one that could drift.
 */
export function BodyMetricsPanel({ clientId, now = calendarToday() }: {
  clientId: string; now?: Date;
}) {
  const [rows, setRows] = useState<Row[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [win, setWin] = useState<Window>("4w");
  /* 🔴 `Update results` WRITES THE TABLE THIS PANEL READS, so a save has to be able to say "read
     it again". A counter rather than a refetch function: the fetch already lives in an effect and
     a second copy of it, called from the save, is two statements of one query. */
  const [seq, setSeq] = useState(0);
  const [writing, setWriting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from("body_measurements")
        .select(`measured_at, weight_kg, body_fat_percent, ${WEEK_COLUMNS}`)
        .eq("user_id", clientId)
        .order("measured_at");
      if (cancelled) return;
      if (error !== null) { setFailed(true); return; }
      /* ⚠️ COERCED HERE TOO, AND IT WAS ONLY WORKING BY LUCK. `body_measurements.weight_kg` is
         `numeric`, so PostgREST sends `"76.50"` — and the card rendered **"76.50 kg"**, the
         trailing zero being the tell nobody read. Every arithmetic path it reached happened to be
         one JavaScript coerces (`-` on two strings gives a number), so the delta was right and
         the chart drew; `.toFixed` on the raw value would not have been. A number that is
         secretly a string is a defect waiting for the first consumer that does not coerce. */
      setRows((data ?? []).map((r) => ({
        measuredAtISO: r.measured_at,
        weightKg: wireNumber(r.weight_kg),
        bodyFatPercent: wireNumber(r.body_fat_percent),
        week: weekAnswerLines(r),
        water: waterTagText(r),
      })));
    })();
    return () => { cancelled = true; };
  }, [clientId, seq]);

  const dated = useMemo(
    () => (rows ?? []).map((m) => ({ ...m, atISO: m.measuredAtISO })), [rows]);
  const within = useMemo(() => inWindow(dated, win, now), [dated, win, now]);

  const series = (pick: (m: Measurement) => number | null) =>
    within.flatMap((m) => {
      const v = pick(m);
      return v === null ? [] : [{ atMs: new Date(m.measuredAtISO).getTime(), value: v }];
    });

  /* THE LATEST WEEK THAT WAS ANSWERED, whatever the window — a coach asks "how was their week",
     and a window that happens to exclude the last check-in must not answer "nothing". */
  const lastWeek = useMemo(() => {
    for (let i = (rows ?? []).length - 1; i >= 0; i--) {
      const r = (rows ?? [])[i];
      if (r !== undefined && r.week.length > 0) return r;
    }
    return null;
  }, [rows]);

  const weight = series((m) => m.weightKg);
  const fat = series((m) => m.bodyFatPercent);

  /* 🔴 THE WRITE DOOR IS DRAWN IN EVERY STATE THAT HAS A CLIENT — AND THE EMPTY ONE IS THE
     STATE THAT NEEDS IT MOST. This panel used to return its three sentences BEFORE any control,
     so a client with no measurements offered a coach nothing to do about it: the empty state was
     a one-way door, which is the shape §29.8's flyout header is the codex's name for. `Update
     results` is rendered above the branch and the branch renders underneath it. */
  const door = (
    <>
      {/* W72 step 7 — a TEXT action (Proposal 7 rule 6: secondary outlined, tertiary as text); the grey filled button
          was the copied control. */}
      <button type="button" className="ours-text-action measure-open"
              onClick={() => setWriting(true)}>Update results</button>
      <UpdateResultsWindow
        clientId={clientId} todayKey={dayKeyOf(now)}
        open={writing}
        onClose={() => setWriting(false)}
        onSaved={() => setSeq((n) => n + 1)} />
    </>
  );

  /* ⚠️ NOT LOADED, FAILED AND EMPTY ARE THREE ANSWERS. A blank panel for any of them would be a
     silence a coach reads as "this client records nothing". */
  if (failed) {
    return <p className="ht-callout results-empty" role="alert">
      Their measurements could not be read. This is the panel, not their data.
    </p>;
  }
  /* ⚠️ NO DOOR WHILE IT LOADS, AND NO DOOR ON A FAILURE. A form over a list that has not arrived
     invites a coach to add a reading beside one they cannot see, and a panel that could not READ
     the table has no business offering to WRITE it. */
  if (rows === null) return <p className="ht-caption metric-tile-empty">Loading measurements…</p>;
  if (rows.length === 0) {
    return (
      <section className="body-metrics" aria-labelledby="body-metrics-h">
        <h3 id="body-metrics-h" className="ht-overline">Body metrics</h3>
        <p className="ht-callout results-empty">
          No measurements recorded. Weight and body fat appear here as soon as one is saved.
        </p>
        {door}
      </section>
    );
  }

  return (
    <section className="body-metrics" aria-labelledby="body-metrics-h">
      <div className="body-metrics-head">
        <h3 id="body-metrics-h" className="ht-overline">Body metrics</h3>
        {/* Beside the heading, as theirs is beside the window selector — an OUTLINED button, not
            the accent: §13 caps a surface at one primary and this tab's is not here. */}
        {door}
      </div>

      {/* THE WINDOW PICKER, ONE ROW ABOVE THE CHARTS — it governs both of them and the table, so
          it belongs above all three rather than repeated on each. */}
      {/* W72 step 7 — A FILTER ROW, so CHIPS (Proposal 7 rule 6): the chosen window is ink-filled, never accent text. */}
      <div className="ours-chips metric-windows" role="group" aria-label="Window">
        {WINDOWS.map((w) => (
          <button key={w} type="button" className="ours-chip"
                  aria-pressed={w === win}
                  onClick={() => setWin(w)}>{windowLabel(w)}</button>
        ))}
      </div>

      {lastWeek !== null && (
        <div className="week-check-in" aria-labelledby="week-check-in-h">
          <h4 id="week-check-in-h" className="ht-caption">
            {/* "LAST CHECK-IN", NEVER "THIS WEEK": the latest answered row may be weeks old, and
                its date is the truth the coach needs (rendered 2026-10-09: a 5 Sep row read "This week"). */}
            Last check-in · {longDate(lastWeek.measuredAtISO.slice(0, 10))}
          </h4>
          <dl className="week-check-in-lines">
            {lastWeek.week.map((l) => (
              <div key={l.key}><dt>{l.label}</dt><dd>{l.value}</dd></div>
            ))}
          </dl>
        </div>
      )}

      <div className="metric-multiples">
        <OneMeasure label="Weight" unit="kg" points={weight}
                    values={within.map((m) => ({ atISO: m.measuredAtISO, value: m.weightKg }))} />
        <OneMeasure label="Body fat" unit="%" points={fat}
                    values={within.map((m) => ({ atISO: m.measuredAtISO, value: m.bodyFatPercent }))} />
      </div>

      {/* A TABLE FOR EVERY CHART — the rule, and the thing a coach actually copies a number out of. */}
      <table className="metric-table">
        <caption className="ht-caption">Entries in this window</caption>
        <thead>
          <tr><th scope="col">Date</th><th scope="col" className="ours-num">Weight</th><th scope="col" className="ours-num">Body fat</th></tr>
        </thead>
        <tbody>
          {within.length === 0 ? (
            <tr><td colSpan={3} className="ht-caption">Nothing recorded in this window.</td></tr>
          ) : [...within].reverse().map((m) => (
            <tr key={m.measuredAtISO}>
              <td>{longDate(m.measuredAtISO.slice(0, 10))}</td>
              {/* ⚠️ AN EM DASH, NEVER A ZERO. A body fat nobody measured is not 0 %. */}
              <td className="ours-num">
                {m.weightKg === null ? "—" : `${m.weightKg} kg`}
                {/* The water tag qualifies THIS weight, so it is said in its cell — never alone. */}
                {m.weightKg !== null && m.water !== null && <span className="ht-caption water-tag"> · {m.water}</span>}
              </td>
              <td className="ours-num">{m.bodyFatPercent === null ? "—" : `${m.bodyFatPercent} %`}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

/** One measure: its one-answer line, then its own chart (or tile). */
function OneMeasure({ label, unit, points, values }: {
  label: string; unit: string;
  points: { atMs: number; value: number }[];
  values: { atISO: string; value: number | null }[];
}) {
  const answer = oneAnswer(values);
  return (
    <div className="metric-multiple">
      {/* THE CARD'S ONE ANSWER — a delta ONLY when the window holds two readings. */}
      <p className="ht-caption metric-answer">
        {answer.kind === "none" && <span className="metric-answer-none">No {label.toLowerCase()} in this window</span>}
        {answer.kind === "single" && <><strong>{answer.value} {unit}</strong> — one reading, nothing to compare</>}
        {answer.kind === "delta" && (
          <><strong>{answer.value} {unit}</strong>{" "}
            <span className="metric-answer-delta">{describeDelta(answer.delta, unit)} since {longDate(answer.sinceISO.slice(0, 10))}</span></>
        )}
      </p>
      <MetricChart series={{ points, label }} unit={unit} />
    </div>
  );
}
